import { destination, point, multiPolygon, type Units } from '@turf/turf';
import type { Coordinate } from '../coordinate';
import { VehicleSymbol } from '../../../vehicles/domain/vehicleSymbol';
import { arrowShape } from './arrowShape';
import { wheelLoaderFrontGrappleShape } from './wheelLoaderFrontGrappleShape';
import { wheelLoaderHighGrappleShape } from './wheelLoaderHighGrappleShape';
import { timberCraneShape } from './timberCraneShape';
import { coilForkliftShape } from './coilForkliftShape';
import { logStackerShape } from './logStackerShape';

export interface VehicleShapeOptions {
  coordinate: Coordinate;
  properties: GeoJSON.GeoJsonProperties;
  heading: number | null;
  symbol?: VehicleSymbol;
}

export interface PolygonColorConfig {
  type: 'dynamic' | 'fixed';
  color?: string; // Used when type is 'fixed'
}

export interface VehicleShapeDefinition {
  vehicle: string;
  coordinateSystem: '2D';
  unit: 'normalized_percent';
  polygons: {
    [key: string]: Array<[number, number]>;
  };
  colorConfig?: {
    [polygonName: string]: PolygonColorConfig;
  };
}

const turfOptions = { units: 'meters' as Units };

/**
 * Configuration for vehicle dimensions and origin points
 */
interface VehicleConfig {
  vehicleLength: number; // meters, along the Y-axis
  vehicleWidth: number; // meters, along the X-axis
  originX: number; // origin X in normalized space (0-100)
  originY: number; // origin Y in normalized space (0-100)
}

/**
 * Default vehicle configuration
 */
const defaultVehicleConfig: VehicleConfig = {
  vehicleLength: 6,
  vehicleWidth: 4,
  originX: 50,
  originY: 0, // Default arrow tip
};

/**
 * Vehicle-specific configurations
 * Maps VehicleSymbol to its dimensions and origin point
 * originY is set to the grapple/fork grab point for each vehicle
 */
const vehicleConfigs: Partial<Record<VehicleSymbol, VehicleConfig>> = {
  [VehicleSymbol.LogStacker]: {
    vehicleLength: 6,
    vehicleWidth: 4,
    originX: 50,
    originY: 6, // Grapple grab point
  },
  [VehicleSymbol.WheelLoaderFrontGrapple]: {
    vehicleLength: 6,
    vehicleWidth: 4,
    originX: 50,
    originY: 22, // Grapple grab point
  },
  [VehicleSymbol.WheelLoaderHighGrapple]: {
    vehicleLength: 6,
    vehicleWidth: 4,
    originX: 50,
    originY: 7, // Grapple grab point
  },
  [VehicleSymbol.TimberCrane]: {
    vehicleLength: 6,
    vehicleWidth: 4,
    originX: 50,
    originY: -7, // Crane grapple grab point
  },
  [VehicleSymbol.CoilForklift]: {
    vehicleLength: 6,
    vehicleWidth: 4,
    originX: 50,
    originY: 10, // Fork grab point (middle of fork)
  },
  [VehicleSymbol.Undefined]: {
    vehicleLength: 6,
    vehicleWidth: 4,
    originX: 50,
    originY: 0, // Arrow tip
  },
};

/**
 * Get the vehicle configuration for a symbol
 * @param symbol The vehicle symbol
 * @returns The vehicle configuration (dimensions and origin)
 */
function getVehicleConfig(symbol?: VehicleSymbol): VehicleConfig {
  if (symbol && vehicleConfigs[symbol]) {
    return vehicleConfigs[symbol]!;
  }
  return defaultVehicleConfig;
}

/**
 * Get the shape definition for a vehicle symbol
 * @param symbol The vehicle symbol (defaults to arrow shape if undefined)
 * @returns The corresponding shape definition
 */
function getShapeDefinition(symbol?: VehicleSymbol): VehicleShapeDefinition {
  switch (symbol) {
    case VehicleSymbol.LogStacker:
      return logStackerShape;
    case VehicleSymbol.WheelLoaderFrontGrapple:
      return wheelLoaderFrontGrappleShape;
    case VehicleSymbol.WheelLoaderHighGrapple:
      return wheelLoaderHighGrappleShape;
    case VehicleSymbol.TimberCrane:
      return timberCraneShape;
    case VehicleSymbol.CoilForklift:
      return coilForkliftShape;
    case VehicleSymbol.Undefined:
    default:
      return arrowShape;
  }
}

/**
 * Creates a vehicle shape from a shape definition with normalized coordinates
 * Origin is positioned at the grapple/fork grab point for each vehicle type
 * This ensures loads are consistently positioned at the grapple regardless of vehicle symbol
 */
export function createVehicleShape(
  options: VehicleShapeOptions,
  shapeDefinition?: VehicleShapeDefinition,
) {
  const { coordinate, heading, properties, symbol } = options;
  const { longitude, latitude } = coordinate;

  // If no shape definition provided, get it from the symbol
  const shape = shapeDefinition ?? getShapeDefinition(symbol);

  // Get vehicle configuration (dimensions and origin) based on symbol
  const config = getVehicleConfig(symbol);
  const { vehicleLength, vehicleWidth, originX, originY } = config;

  const dir = heading ?? 0;
  const origin = point([longitude, latitude]);

  // Store polygon names and color config for use in styling
  const polygonNames = Object.keys(shape.polygons);
  const colorConfig = shape.colorConfig || {};

  // Convert normalized coordinates to geo coordinates
  // Origin is at (50, 8) in normalized space, which is the grapple center
  const normalizedPolygons = Object.entries(shape.polygons).map(([name, coords]) => {
    const geoCoordinates = coords.map(([x, y]) => {
      // Convert from normalized (0-100) to relative position from origin
      // X: 0 = left edge, 50 = center, 100 = right edge
      // Y: 0 = front, 100 = back
      const relX = ((x - originX) / 100) * vehicleWidth;
      const relY = ((originY - y) / 100) * vehicleLength;

      // Apply transformations: forward (0°), then sideways (90°)
      let geoPoint = origin;

      // Move forward along heading direction
      if (relY !== 0) {
        geoPoint = destination(geoPoint, relY, dir, turfOptions);
      }

      // Move sideways (perpendicular to heading)
      if (relX !== 0) {
        geoPoint = destination(geoPoint, relX, dir + 90, turfOptions);
      }

      return geoPoint.geometry.coordinates;
    });

    // Close the polygon by adding the first point at the end
    geoCoordinates.push(geoCoordinates[0]!);
    return [geoCoordinates]; // Wrap in array for polygon rings
  });

  // Create a MultiPolygon feature containing all parts
  // Add polygon metadata to properties for styling
  const enhancedProperties = {
    ...properties,
    polygonNames,
    colorConfig,
  };

  const multiPoly = multiPolygon(normalizedPolygons, enhancedProperties);

  return multiPoly;
}
